import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Loader2, Search, Plus, Eye, RefreshCw, RotateCcw, FileText, CheckCircle2, Clock, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReturnsOrderList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [returns, setReturns] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  const [filters, setFilters] = useState({
    customer_id: '',
    status: '',
    reference_so: ''
  });

  useEffect(() => {
    loadCustomers();
    loadReturns();
  // eslint-disable-next-line
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers`);
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadReturns = async () => {
    setIsLoading(true);
    try {
      let url = `${BACKEND_URL}/api/otc/returns`;
      const params = new URLSearchParams();
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.reference_so) params.append('reference_so', filters.reference_so);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setReturns(response.data || []);
    } catch (error) {
      console.error('Error loading returns:', error);
      toast.error('Failed to load returns orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    loadReturns();
  };

  const getStatusBadge = (status) => {
    const badges = {
      'CREATED': { color: 'bg-orange-100 text-orange-800', icon: Clock },
      'RECEIVED': { color: 'bg-yellow-100 text-yellow-800', icon: RotateCcw },
      'INSPECTED': { color: 'bg-orange-100 text-orange-800', icon: CheckCircle2 },
      'CREDITED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'CANCELLED': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: FileText };
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
        <Icon className="w-3 h-3" /> {status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="p-6" data-testid="returns-order-list">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Returns Orders</h1>
            <p className="text-sm text-gray-600">Manage customer returns and credit processing</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadReturns} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canWrite('returns') && (
            <Button onClick={() => navigate('/otc/returns/create')} data-testid="create-returns-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Returns Order
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <Select value={filters.customer_id || '__ALL__'} onValueChange={(v) => setFilters({...filters, customer_id: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.customer_id} - {c.customer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  <SelectItem value="CREATED">Created</SelectItem>
                  <SelectItem value="RECEIVED">Received</SelectItem>
                  <SelectItem value="INSPECTED">Inspected</SelectItem>
                  <SelectItem value="CREDITED">Credited</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Original Sales Order</label>
              <Input
                placeholder="SO Number"
                value={filters.reference_so}
                onChange={(e) => setFilters({...filters, reference_so: e.target.value})}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3">Loading returns orders...</span>
            </div>
          ) : returns.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <RotateCcw className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No returns orders found</p>
              {canWrite('returns') && (
              <Button variant="outline" className="mt-4" onClick={() => navigate('/otc/returns/create')}>
                Create First Returns Order
              </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Returns Number</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Original SO</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Return Date</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Return Value</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Credit Value</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Credit Memo</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {returns.map((ret) => (
                    <tr key={ret.returns_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/otc/returns/${ret.returns_number}`)}
                          className="font-mono font-medium text-orange-600 hover:underline"
                        >
                          {ret.returns_number}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-900">{ret.customer_id}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/otc/sales-orders/${ret.reference_so_number}`)}
                          className="font-mono text-orange-600 hover:underline"
                        >
                          {ret.reference_so_number}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{ret.return_date}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        ${ret.total_return_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-green-600">
                        ${ret.total_credit_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(ret.status)}</td>
                      <td className="px-4 py-3">
                        {ret.credit_memo_number ? (
                          <button
                            onClick={() => navigate(`/otc/credit-memos/${ret.credit_memo_number}`)}
                            className="font-mono text-green-600 hover:underline"
                          >
                            {ret.credit_memo_number}
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/otc/returns/${ret.returns_number}`)}
                          data-testid={`view-returns-${ret.returns_number}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default ReturnsOrderList;
